import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Cancel-button action when it must differ from dismiss (X/backdrop). Defaults to onClose. */
  onCancel?: () => void;
  title: string;
  contentText: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  title,
  contentText,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmButtonColor = 'primary',
  isLoading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isLoading) {
          return;
        }
        onClose();
      }}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle
        component='div'
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='h6'
          component='p'
          sx={{ m: 0 }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography>{contentText}</Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        {cancelButtonText && (
          <Button
            size='large'
            variant='outlined'
            onClick={onCancel ?? onClose}
            disabled={isLoading}
          >
            {cancelButtonText}
          </Button>
        )}
        <Button
          size='large'
          onClick={onConfirm}
          variant='contained'
          color={confirmButtonColor}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
